import { useState } from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { hasAnyOngoingRunOnAllDocsCase } from '../../../../../helpers/runs';
import { useJobProgress } from '../../../../../hooks/useJobProgress';
import useAddDocsToMatter from '@/api/mutations/useAddDocsToMatter';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import useGetSubsetOverview from '@/api/queries/useGetSubsetOverview';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FileUploader } from '@/new/components/file-uploader';
import { Icon } from '@/new/components/icon';
import { DocumentUploadOnboarding } from '@/new/components/onboarding/document-upload-onboarding';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';

export function DocumentUpload() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const navigate = useNavigate();
  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? null;
  const { activeJobs } = useJobProgress(allDocumentsCaseId ?? '');
  const { mutateAsync: addDocs, isLoading } = useAddDocsToMatter(matterId);
  const { data: overview } = useGetSubsetOverview(matterId, allDocumentsCaseId ?? '');
  const isRunOngoing = hasAnyOngoingRunOnAllDocsCase(overview?.runs, allDocumentsCaseId ?? '');

  const [filesProcessing, setFilesProcessing] = useState(false);
  const [filesExist, setFilesExist] = useState(false);

  const location = useLocation();
  const fromChecklistDocumentUpload = Boolean(location?.state?.fromChecklistDocumentUpload);

  const handleProcessDocuments = async () => {
    try {
      await addDocs();
      toast.success('Documents are being processed');
      navigate(`/matters/${matterId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add documents');
    }
  };

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                { href: `/matters/${matterId}`, label: matter.name, active: false, iconClass: 'lucide--folder' },
                { href: `/matters/${matterId}/docs/upload`, label: 'Upload Documents', active: true },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Upload Documents" subtext="Add more documents to your matter." />

          <Button
            variant="primary"
            disabled={filesProcessing || !filesExist || isLoading || activeJobs.length > 0 || isRunOngoing}
            onClick={handleProcessDocuments}
          >
            {isLoading ? 'Processing...' : 'Process Documents'}
            <Icon className="lucide--arrow-right" />
          </Button>
        </PageHeader>

        <FileUploader
          setFilesProcessing={setFilesProcessing}
          activeJobs={activeJobs}
          setFilesExist={setFilesExist}
          allDocumentsCaseId={allDocumentsCaseId}
        />
      </PageContainer>
      {fromChecklistDocumentUpload && <DocumentUploadOnboarding />}
    </>
  );
}
